import { createRoute, lazyRouteComponent, Outlet } from '@tanstack/react-router';
import { rootRoute } from './__root';
import { Shell } from '@/components/layout/shell';
import { AppSidebar } from '@/components/layout/app-sidebar';
import { AdminTopbar } from '@/components/layout/admin-topbar';
import { AuthGuard } from '@/security/auth-guard';

// Login route — no shell wrapper
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('@/features/authentication/pages/login-page'),
  ),
});

// App layout — wraps protected pages with Shell + AppHeader
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: function AppLayout() {
    return (
      <AuthGuard>
        <Shell>
          <AppSidebar />
          <main className="flex min-w-0 flex-1 flex-col">
            <AdminTopbar />
            <Outlet />
          </main>
        </Shell>
      </AuthGuard>
    );
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/dashboard',
  component: lazyRouteComponent(
    () => import('@/features/dashboard/pages/admin-dashboard-page'),
  ),
});

const tenantsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tenants',
  component: lazyRouteComponent(
    () => import('@/features/tenants/pages/tenant-list-page'),
  ),
});

const tenantOnboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tenants/new',
  component: lazyRouteComponent(
    () => import('@/features/tenants/pages/tenant-onboard-page'),
  ),
});

const tenantManageRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tenants/$tenantId/manage',
  component: lazyRouteComponent(
    () => import('@/features/tenants/pages/tenant-manage-page'),
  ),
});

const systemTemplatesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/system-templates',
  component: lazyRouteComponent(
    () => import('@/features/system-templates/pages/system-template-list-page'),
  ),
});

const templateCreateRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/system-templates/new',
  component: lazyRouteComponent(
    () => import('@/features/system-templates/pages/template-source-upload-page'),
  ),
});

const templateEditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/system-templates/$templateId/edit',
  component: lazyRouteComponent(
    () => import('@/features/system-templates/pages/template-form-page'),
  ),
});

const templateViewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/system-templates/$templateId/view',
  component: lazyRouteComponent(
    () => import('@/features/system-templates/pages/template-form-page'),
  ),
});

const templateCopyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/system-templates/$templateId/copy',
  component: lazyRouteComponent(
    () => import('@/features/system-templates/pages/template-form-page'),
  ),
});

const aiUsageRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/ai-usage',
  component: lazyRouteComponent(
    () => import('@/features/ai-usage/pages/ai-usage-page'),
  ),
});

const dropFolderMonitorRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/drop-folders',
  component: lazyRouteComponent(
    () => import('@/features/drop-folder/pages/drop-folder-monitor-page'),
  ),
});

const billingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/billing',
  component: lazyRouteComponent(
    () => import('@/features/billing/pages/admin-billing-status-page'),
  ),
});

const resellerTenantCreditsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/tenants/$tenantId/credits',
  validateSearch: (search: Record<string, unknown>): { setupBilling?: boolean } => ({
    setupBilling: search.setupBilling === true || search.setupBilling === 'true' ? true : undefined,
  }),
  component: lazyRouteComponent(
    () => import('@/features/billing/pages/reseller-tenant-credits-page'),
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/users',
  component: lazyRouteComponent(
    () => import('@/features/users/pages/admin-users-page'),
  ),
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  appLayoutRoute.addChildren([
    dashboardRoute,
    tenantsRoute,
    tenantOnboardRoute,
    tenantManageRoute,
    billingRoute,
    resellerTenantCreditsRoute,
    usersRoute,
    systemTemplatesRoute,
    templateCreateRoute,
    templateEditRoute,
    templateViewRoute,
    templateCopyRoute,
    aiUsageRoute,
    dropFolderMonitorRoute,
  ]),
]);
